import { useContext } from "react";

import { Navigate, Route as RouterRoute } from "react-router";

import {
    CorporateFareIcon,
    DashboardOutlineIcon,
    DesktopMacIcon,
    DevicesFotaIcon,
    FileDraftOutlineIcon,
    GroupOutlineIcon,
    ModelOutlinedIcon,
    QrCodeIcon,
    TaskGropsIcon,
    WebTaskGroupsIcon
} from "@dotnet/telematics-design-system";

import Login from "components/Auth/Login";
import RequestReset from "components/Auth/PasswordReset/RequestReset";
import Reset from "components/Auth/Reset/Reset";
import Signup from "components/Auth/SignUp/Signup";
import Companies from "components/Companies";
import Dashboard from "components/Dashboard";
import DeviceGroups from "components/DeviceGroups";
import DeviceModels from "components/DeviceModels";
import Devices from "components/Devices";
import ErrorPageTct from "components/ErrorPageTct";
import Files from "components/Files";
import Licenses from "components/Licenses";
import Maintenance from "components/Maintenance";
import ProductCodes from "components/ProductCodes";
import TaskGroups from "components/TaskGroups";
import Users from "components/Users";
import AuthContext from "context/auth/authContext";
import {
    COMPANIES,
    DASHBOARD,
    DESKTOP_LICENSES,
    DEVICE_MODELS,
    DEVICES,
    FILES,
    GROUPS,
    PRODUCT_CODES,
    ROUTES,
    TASKS,
    USERS
} from "shared";

import PrivateRoute from "../PrivateRoute";
import { Route } from "../types";

const useRoutes = () => {
    const { isAdminUser, isSupportUser, isSuperUser } = useContext(AuthContext);

    const routes: Route[] = [
        { path: ROUTES.Login, component: Login },
        { path: ROUTES.RequestReset, component: RequestReset },
        { path: ROUTES.Reset, component: Reset },
        { path: ROUTES.Signup, component: Signup },
        { path: ROUTES.Maintenance, component: Maintenance },
        { path: ROUTES.Error, component: ErrorPageTct },
        {
            path: ROUTES.Dashboard,
            component: Dashboard,
            isPrivate: true,
            navigation: {
                name: DASHBOARD,
                icon: <DashboardOutlineIcon />
            }
        },
        {
            path: ROUTES.Devices,
            component: Devices,
            isPrivate: true,
            navigation: {
                name: DEVICES,
                icon: <DevicesFotaIcon />
            }
        },
        {
            path: ROUTES.TaskGroups,
            component: TaskGroups,
            isPrivate: true,
            navigation: {
                name: TASKS,
                icon: <TaskGropsIcon />
            }
        },
        {
            path: ROUTES.DeviceGroups,
            component: DeviceGroups,
            isPrivate: true,
            navigation: {
                name: GROUPS,
                icon: <WebTaskGroupsIcon />
            }
        },
        {
            path: ROUTES.Files,
            component: Files,
            isPrivate: true,
            navigation: {
                name: FILES,
                icon: <FileDraftOutlineIcon />
            }
        },
        {
            path: ROUTES.Companies,
            component: Companies,
            isPrivate: true,
            navigation: {
                name: COMPANIES,
                icon: <CorporateFareIcon />
            }
        },
        {
            path: ROUTES.Users,
            component: Users,
            isPrivate: true,
            navigation: {
                name: USERS,
                icon: <GroupOutlineIcon />
            }
        },
        { path: ROUTES.Roles, component: Users, isPrivate: true },
        {
            path: ROUTES.ProductCodes,
            component: ProductCodes,
            isPrivate: true,
            isRestricted: !isSupportUser,
            navigation: {
                name: PRODUCT_CODES,
                icon: <QrCodeIcon />,
                hasSeparator: true
            }
        },
        {
            path: ROUTES.DesktopLicenses,
            component: Licenses,
            isPrivate: true,
            isRestricted: !isAdminUser,
            navigation: {
                name: DESKTOP_LICENSES,
                icon: <DesktopMacIcon />
            }
        },
        {
            path: ROUTES.DeviceModels,
            component: DeviceModels,
            isPrivate: true,
            isRestricted: !isSuperUser,
            navigation: {
                name: DEVICE_MODELS,
                icon: <ModelOutlinedIcon />
            }
        },
        {
            path: ROUTES.DeviceModelTaskCompatibilities,
            component: DeviceModels,
            isPrivate: true,
            isRestricted: !isSuperUser
        }
    ];

    const renderRoutes = () => (
        <>
            {routes.map(
                ({ path, component: Component, isPrivate, isRestricted }) => (
                    <RouterRoute
                        key={path}
                        path={path}
                        element={
                            isPrivate ? (
                                <PrivateRoute
                                    component={<Component />}
                                    isRestricted={isRestricted}
                                />
                            ) : (
                                <Component />
                            )
                        }
                    />
                )
            )}

            <RouterRoute
                path={ROUTES.NotFound}
                element={<Navigate to={{ pathname: ROUTES.Login }} replace />}
            />
        </>
    );

    return { routes, renderRoutes };
};

export { useRoutes };
